import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useCallback, useEffect, useRef, useState } from "react";
import { useOrganizationStore } from "@/stores/organization";
import { useUserStore } from "@/stores/user";
import AvatarPopover from "./avatar-popover";
import { getRandomOrgAvatar } from "@/utils/utils";
import { createOrganizationMember } from "@/services/member.client";

const DIALOG_TITLE = "What's your organization's name?";
const PLACEHOLDER_TEXT = "Libum Credit Union";

interface OrganizationCreateDialogProps {
  controlledOpen?: boolean;
  isForced?: boolean;
  onSuccess?: (organizationId: string) => void;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
}

export default function OrganizationCreateDialog({
  controlledOpen,
  isForced,
  onSuccess,
  onOpenChange,
  trigger,
}: OrganizationCreateDialogProps) {
  const { user, updateUser } = useUserStore();
  const { createOrganization } = useOrganizationStore();
  const [name, setName] = useState("");
  const [avatar, setAvatar] = useState(getRandomOrgAvatar());
  const [internalOpen, setInternalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (controlledOpen === undefined) {
        setInternalOpen(open);
      }
      if (onOpenChange) {
        onOpenChange(open);
      }
    },
    [controlledOpen, onOpenChange],
  );

  useEffect(() => {
    if (!isOpen && inputRef.current) {
      const dialogElement = inputRef.current.closest('[data-state="open"]');
      const handleTransitionEnd = () => {
        setName("");
        setAvatar(getRandomOrgAvatar());
        setIsSubmitted(false);
      };

      dialogElement?.addEventListener("transitionend", handleTransitionEnd);
      return () =>
        dialogElement?.removeEventListener(
          "transitionend",
          handleTransitionEnd,
        );
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitted(true);
      const organization = await createOrganization(name.trim(), avatar);

      if (user && organization) {
        await updateUser({
          ...user,
          organizations: [...user.organizations, organization.id],
        });
        await createOrganizationMember(
          user.id,
          organization.id,
          "UNASSIGNED",
          "UNASSIGNED",
          "admin",
          "active",
        );
      }

      setIsOpen(false);

      if (onSuccess) {
        onSuccess(organization.id);
      }
    } catch (error: any) {
      console.warn(error.message);
      setIsSubmitted(false);
    }
  }, [
    avatar,
    name,
    createOrganization,
    onSuccess,
    setIsOpen,
    updateUser,
    user,
  ]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setName(newValue);
  };

  const handleAvatarChange = (src: string) => {
    setAvatar(src);
  };

  useEffect(() => {
    let frameId: number;

    if (isOpen) {
      frameId = requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      });
    }

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

      <DialogContent
        className="bg-card"
        onPointerDownOutside={(e) => isForced === true && e.preventDefault()}
      >
        <DialogHeader className="flex">
          <DialogTitle className="text-center text-xl text-gray-1100 dark:text-gray-100">
            {DIALOG_TITLE}
          </DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <div className="flex items-center py-2">
          <AvatarPopover
            avatar={avatar}
            onAvatarChange={handleAvatarChange}
            type="organization"
          />
          <Input
            ref={inputRef}
            id="org-name"
            type="text"
            value={name}
            className="mt-0"
            onChange={handleInputChange}
            placeholder={PLACEHOLDER_TEXT}
          />
        </div>
        <DialogFooter>
          <Button
            className="w-full"
            size="lg"
            disabled={!name.trim() || isSubmitted}
            onClick={handleSubmit}
          >
            {isSubmitted
              ? "Creating organization..."
              : `${isForced ? "Get started" : "Create organization"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
